import { Checkbox, Empty, Flex, FormInstance, List, Space, Spin } from "antd"
import { memo, useEffect, useMemo, useState } from "react"
import Input from "antd/es/input/Input"
import { useTypedSelector } from "../hooks/useTypedSelector"

interface ProjectModalCampaignsProps {
  form: FormInstance<any>
  setCampaignsForm: (campaignIds: number[]) => void
}

const ProjectModalCampaigns: React.FC<ProjectModalCampaignsProps> = memo(
  function ProjectModalCampaigns({ form, setCampaignsForm }) {
    const [inputValue, setInputValue] = useState<string>()
    const [selectedCampaigns, setSelectedCampaigns] = useState<number[]>([])
    const [isCheckAllSelect, setIsCheckAllSelect] = useState<boolean>(false)

    const { campaigns, loaded } = useTypedSelector((state) => state.campaigns)
    const { projectData } = useTypedSelector((state) => state.projectDataSettings)

    const selectOptions = campaigns.map((campaign) => ({
      label: campaign.campaignName,
      value: Number(campaign.campaignId),
      key: campaign.campaignName + campaign.campaignId,
    }))

    const searchedCampaigns = useMemo(() => {
      if (inputValue) {
        return selectOptions.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        )
      } else {
        setIsCheckAllSelect(false)
      }

      return selectOptions
    }, [inputValue])

    useEffect(() => {
      setSelectedCampaigns(projectData.campaignIds)
    }, [projectData])

    function onChangeSelectCampaign(isChange: boolean, campaignId: number) {
      const alreadySavedCampaignIds = form.getFieldValue("campaigns")

      if (isChange) {
        setSelectedCampaigns((prev) => [...prev, campaignId])
        if (alreadySavedCampaignIds) {
          setCampaignsForm([...alreadySavedCampaignIds, campaignId])
        } else if (campaignId) {
          setCampaignsForm([campaignId])
        }
        return
      }

      setSelectedCampaigns((prev) => prev.filter((prevCampaignId) => prevCampaignId !== campaignId))
      setCampaignsForm(
        alreadySavedCampaignIds.filter(
          (alreadySavedCampaignId) => alreadySavedCampaignId !== campaignId
        )
      )
    }

    function isCheckedCampaign(campaignId: number) {
      return selectedCampaigns.some((selectCampaignId) => selectCampaignId === campaignId)
    }

    function handleSelectAllVisible(checked: boolean) {
      setIsCheckAllSelect(checked)
      const campaignIds: number[] = form.getFieldValue("campaigns")
      const searchedCampaignIds = searchedCampaigns.map((campaign) => campaign.value)

      if (checked) {
        setSelectedCampaigns((prev) => [...prev, ...searchedCampaignIds])
        if (campaignIds) {
          setCampaignsForm([...campaignIds, ...searchedCampaignIds.map((campaignId) => campaignId)])
        } else {
          setCampaignsForm([...searchedCampaignIds.map((campaign) => campaign)])
        }
        return
      }
      setSelectedCampaigns((prev) =>
        prev.filter((prevCampaign) => !searchedCampaignIds.includes(prevCampaign))
      )
      setCampaignsForm(campaignIds.filter((campaignId) => searchedCampaignIds.includes(campaignId)))
    }

    return loaded ? (
      <Spin />
    ) : (
      <Flex vertical={true} gap="16px">
        <Input
          placeholder="Search campaign by name"
          onChange={(value) => setInputValue(value.target.value)}
          value={inputValue}
        />

        <Space>
          <Checkbox
            onChange={(value) => handleSelectAllVisible(value.target.checked)}
            checked={isCheckAllSelect}
          />
          Select all visible
        </Space>

        <List style={{ height: "200px", overflowY: "auto" }}>
          {searchedCampaigns.length ? (
            searchedCampaigns.map((option) => (
              <List.Item key={option.value}>
                <Space>
                  <Checkbox
                    onChange={(value) => onChangeSelectCampaign(value.target.checked, option.value)}
                    checked={isCheckedCampaign(option.value)}
                  />
                  {option.label}
                </Space>
              </List.Item>
            ))
          ) : (
            <Empty />
          )}
        </List>
      </Flex>
    )
  }
)

export default ProjectModalCampaigns
